import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Save, Eye } from "lucide-react";
import { PageHeader } from "@/components/platform/platformUi";
import RichTextEditor from "@/components/blog/RichTextEditor";

const emptyForm = {
    title: "",
    slug: "",
    excerpt: "",
    body_html: "",
    cover_image_url: "",
    status: "draft",
    tags: "",
    seo_title: "",
    seo_description: "",
};

function slugFromTitle(title) {
    return title
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

export default function PlatformBlogEditor() {
    const { postId } = useParams();
    const navigate = useNavigate();
    const isNew = postId === "new";
    const [form, setForm] = useState(emptyForm);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(!isNew);
    const [slugTouched, setSlugTouched] = useState(false);

    useEffect(() => {
        if (isNew) return;
        setLoading(true);
        api.get(`/platform/blog/${postId}`)
            .then((r) => {
                const p = r.data;
                setForm({
                    title: p.title || "",
                    slug: p.slug || "",
                    excerpt: p.excerpt || "",
                    body_html: p.body_html || "",
                    cover_image_url: p.cover_image_url || "",
                    status: p.status || "draft",
                    tags: (p.tags || []).join(", "),
                    seo_title: p.seo_title || "",
                    seo_description: p.seo_description || "",
                });
                setSlugTouched(true);
            })
            .catch(() => {
                toast.error("Could not load post");
                navigate("/platform/blog");
            })
            .finally(() => setLoading(false));
    }, [isNew, postId, navigate]);

    const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

    const onTitleChange = (title) => {
        set("title", title);
        if (!slugTouched) set("slug", slugFromTitle(title));
        if (!form.seo_title) set("seo_title", title.slice(0, 70));
    };

    const submit = async (publishOverride) => {
        if (!form.title.trim()) {
            toast.error("Title is required");
            return;
        }
        if (!form.body_html.trim() || form.body_html === "<p><br></p>") {
            toast.error("Article body is required");
            return;
        }
        setBusy(true);
        const status = publishOverride || form.status;
        const payload = {
            title: form.title.trim(),
            slug: form.slug.trim() || undefined,
            excerpt: form.excerpt.trim(),
            body_html: form.body_html,
            cover_image_url: form.cover_image_url.trim(),
            status,
            tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
            seo_title: form.seo_title.trim(),
            seo_description: form.seo_description.trim(),
        };
        try {
            if (isNew) {
                const r = await api.post("/platform/blog", payload);
                toast.success(status === "published" ? "Post published" : "Draft saved");
                navigate(`/platform/blog/${r.data.id}`, { replace: true });
            } else {
                await api.patch(`/platform/blog/${postId}`, payload);
                toast.success(status === "published" ? "Post published" : "Post saved");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-[40vh] items-center justify-center p-6">
                <p className="text-[#8A8178]">Loading editor…</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="platform-blog-editor">
            <div className="flex flex-wrap items-center gap-3">
                <Link
                    to="/platform/blog"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#8A8178] hover:text-[#4A0E17]"
                >
                    <ArrowLeft className="size-4" />
                    Back to blog
                </Link>
            </div>

            <PageHeader
                eyebrow="Content"
                title={isNew ? "New blog post" : "Edit blog post"}
                subtitle="Write for salon owners — rich text, cover image, and SEO fields"
                action={
                    <div className="flex flex-wrap gap-2">
                        {!isNew && form.status === "published" && form.slug && (
                            <a href={`/blog/${form.slug}`} target="_blank" rel="noreferrer">
                                <Button type="button" variant="outline">
                                    <Eye className="mr-1.5 size-4" />
                                    Preview
                                </Button>
                            </a>
                        )}
                        <Button type="button" variant="outline" disabled={busy} onClick={() => submit("draft")}>
                            Save draft
                        </Button>
                        <Button
                            type="button"
                            disabled={busy}
                            className="bg-[#4A0E17] text-white hover:bg-[#3a0b12]"
                            onClick={() => submit("published")}
                        >
                            <Save className="mr-1.5 size-4" />
                            {form.status === "published" ? "Update & publish" : "Publish"}
                        </Button>
                    </div>
                }
            />

            <div className="grid gap-6 lg:grid-cols-12">
                <div className="space-y-5 lg:col-span-8">
                    <div className="space-y-2">
                        <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">Title</Label>
                        <Input
                            value={form.title}
                            onChange={(e) => onTitleChange(e.target.value)}
                            placeholder="How to grow salon revenue in 2026"
                            className="border-[#E8E4DE] bg-[#FFFEF9]"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">Content</Label>
                        <RichTextEditor
                            value={form.body_html}
                            onChange={(v) => set("body_html", v)}
                            minHeight={360}
                        />
                    </div>
                </div>

                <div className="space-y-5 lg:col-span-4">
                    <div className="rounded-xl border border-[#E8E4DE] bg-white p-5 shadow-sm">
                        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#C9A227]">Publishing</p>
                        <div className="mt-4 space-y-4">
                            <div className="space-y-2">
                                <Label className="text-xs font-semibold text-[#3D2817]">URL slug</Label>
                                <Input
                                    value={form.slug}
                                    onChange={(e) => {
                                        setSlugTouched(true);
                                        set("slug", e.target.value);
                                    }}
                                    placeholder="grow-salon-revenue"
                                    className="border-[#E8E4DE] bg-[#FFFEF9] text-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-semibold text-[#3D2817]">Status</Label>
                                <select
                                    className="h-10 w-full rounded-md border border-[#E8E4DE] bg-[#FFFEF9] px-3 text-sm"
                                    value={form.status}
                                    onChange={(e) => set("status", e.target.value)}
                                >
                                    <option value="draft">Draft</option>
                                    <option value="published">Published</option>
                                </select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-semibold text-[#3D2817]">Cover image URL</Label>
                                <Input
                                    value={form.cover_image_url}
                                    onChange={(e) => set("cover_image_url", e.target.value)}
                                    placeholder="https://…"
                                    className="border-[#E8E4DE] bg-[#FFFEF9] text-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-semibold text-[#3D2817]">Tags (comma separated)</Label>
                                <Input
                                    value={form.tags}
                                    onChange={(e) => set("tags", e.target.value)}
                                    placeholder="salon, marketing, gst"
                                    className="border-[#E8E4DE] bg-[#FFFEF9] text-sm"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="rounded-xl border border-[#E8E4DE] bg-white p-5 shadow-sm">
                        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#C9A227]">SEO</p>
                        <div className="mt-4 space-y-4">
                            <div className="space-y-2">
                                <Label className="text-xs font-semibold text-[#3D2817]">Meta title</Label>
                                <Input
                                    value={form.seo_title}
                                    onChange={(e) => set("seo_title", e.target.value)}
                                    maxLength={70}
                                    className="border-[#E8E4DE] bg-[#FFFEF9] text-sm"
                                />
                                <p className="text-[10px] text-[#8A8178]">{form.seo_title.length}/70</p>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-semibold text-[#3D2817]">Meta description</Label>
                                <Textarea
                                    value={form.seo_description}
                                    onChange={(e) => set("seo_description", e.target.value)}
                                    maxLength={160}
                                    rows={3}
                                    className="border-[#E8E4DE] bg-[#FFFEF9] text-sm"
                                />
                                <p className="text-[10px] text-[#8A8178]">{form.seo_description.length}/160</p>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-semibold text-[#3D2817]">Excerpt</Label>
                                <Textarea
                                    value={form.excerpt}
                                    onChange={(e) => set("excerpt", e.target.value)}
                                    rows={3}
                                    placeholder="Short summary for blog cards and search"
                                    className="border-[#E8E4DE] bg-[#FFFEF9] text-sm"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
